import { FileCard, FileGrid } from '@qse/ui'
import { type FileItem } from '@qse/utils'
import React from 'react'

type ViewType = 'grid' | 'list'

interface FileExplorerContentProps {
  files: FileItem[]
  viewMode: ViewType
  selectedFiles: Set<string | number>
  draggedFiles: (string | number)[]
  dragOverIndex: number | null
  trashMode?: boolean
  fileRefs: React.MutableRefObject<Map<string | number, HTMLDivElement>>
  onFileClick: (fileId: string | number, e: React.MouseEvent) => void
  onFileDoubleClick: (fileId: string | number) => void
  onFileDrop: (e: React.DragEvent<Element>, index: number) => void
  onDragStart: (e: React.DragEvent<Element>, fileId?: string | number) => void
  onDragEnd: () => void
  onDragEnter: (e: React.DragEvent<Element>) => void
  onDragLeave: () => void
  onDragOver: (e: React.DragEvent<Element>, index: number) => void
  onContextMenu: (e: React.MouseEvent, fileId: string | number) => void
  onSelectionChange: (fileId: string | number, selected: boolean) => void
  // Action handlers
  onCopy: (fileIds: string[]) => void
  onDelete: (fileIds: string[]) => void
  onDownload: (fileIds: string[]) => void
  onRename: (fileIds: string[], path?: string) => void
  onToggleFavorite: (fileIds: string[]) => void
  onRestore: (file: FileItem) => Promise<void>
  onDeleteForever: (file: FileItem) => Promise<void>
  onDeleteGhostFiles?: (fileIds: string[]) => Promise<void>
  onMove: (file: FileItem) => void
}

const FileExplorerContent: React.FC<FileExplorerContentProps> = ({
  files,
  viewMode,
  selectedFiles,
  draggedFiles,
  dragOverIndex,
  trashMode = false,
  fileRefs,
  onFileClick,
  onFileDoubleClick,
  onFileDrop,
  onDragStart,
  onDragEnd,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onContextMenu,
  onSelectionChange,
  onCopy,
  onDelete,
  onDownload,
  onRename,
  onToggleFavorite,
  onRestore,
  onDeleteForever,
  onDeleteGhostFiles,
  onMove,
}) => {
  if (viewMode === 'grid') {
    return (
      <FileGrid
        dragOverIndex={dragOverIndex}
        draggedFiles={draggedFiles}
        fileRefs={fileRefs}
        files={files}
        selectedFiles={selectedFiles}
        trashMode={trashMode}
        onContextMenu={onContextMenu}
        onDragEnd={onDragEnd}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDragStart={onDragStart}
        onFileClick={onFileClick}
        onFileDoubleClick={onFileDoubleClick}
        onFileDrop={onFileDrop}
      />
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {files.map((file, index) => (
        <FileCard
          key={file.id}
          draggedFiles={draggedFiles}
          file={file}
          isDragOver={dragOverIndex === index}
          isDragging={draggedFiles.includes(file.id)}
          isSelected={selectedFiles.has(file.id)}
          trashMode={trashMode}
          variant={viewMode}
          innerRef={el => {
            if (el) {
              fileRefs.current.set(file.id, el)
              el.setAttribute('data-file-id', file.id.toString())
            } else {
              fileRefs.current.delete(file.id)
            }
          }}
          isFolderDrop={
            file.type === 'folder' && dragOverIndex === index && draggedFiles.length > 0
          }
          onClick={e => onFileClick(file.id, e)}
          onCopy={() => onCopy([file.id.toString()])}
          onDelete={() => onDelete([file.id.toString()])}
          onDeleteForever={onDeleteForever}
          onDeleteGhostFiles={onDeleteGhostFiles}
          onDoubleClick={() => onFileDoubleClick(file.id)}
          onDownload={() => onDownload([file.id.toString()])}
          onDragEnd={onDragEnd}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDragOver={e => onDragOver(e, index)}
          onDragStart={onDragStart}
          onDrop={(e: React.DragEvent<Element>) => onFileDrop(e, index)}
          onMove={onMove}
          onRename={() => onRename([file.id.toString()], file.path || '')}
          onRestore={onRestore}
          onSelectionChange={onSelectionChange}
          onToggleFavorite={() => onToggleFavorite([file.id.toString()])}
          onContextMenu={e => {
            e.preventDefault()
            if (!selectedFiles.has(file.id)) {
              onSelectionChange(file.id, true)
            }
            onContextMenu(e, file.id)
          }}
        />
      ))}
    </div>
  )
}

export default FileExplorerContent
